import React, { useEffect, useState } from 'react';

import { Badge, Button, Modal, SearchableDropdown } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import { exportTemplatesAPI } from '@/services/api/exportTemplates';

interface Props {
  isOpen: boolean;
  /** The template being assigned (fixed for this dialog). */
  templateKey: string;
  /** The client that currently owns it, or null when it's global. */
  currentClientId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

const labelCls =
  'mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500';

/**
 * Assign a template to a client (or leave it global = all clients). Templates
 * are code-registered; this only sets which client OWNS one. Blank client =
 * available to every client.
 */
const MappingFormModal: React.FC<Props> = ({
  isOpen,
  templateKey,
  currentClientId,
  onClose,
  onSaved,
}) => {
  const [clientId, setClientId] = useState('');
  const [busy, setBusy] = useState(false);
  const providers = useProvidersDropdown(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setClientId(currentClientId != null ? String(currentClientId) : '');
    setBusy(false);
  }, [isOpen, currentClientId]);

  const save = async () => {
    const cid = clientId ? Number(clientId) : null;
    setBusy(true);
    try {
      if (cid) {
        await exportTemplatesAPI.upsertMapping(templateKey, cid);
        showSuccessToast(
          'Saved',
          `"${templateKey}" is now assigned to client ${cid}.`
        );
      } else if (currentClientId != null) {
        // Cleared the owner → make it global (available to all clients).
        await exportTemplatesAPI.deleteMapping(templateKey);
        showSuccessToast(
          'Saved',
          `"${templateKey}" is now available to all clients.`
        );
      }
      onSaved();
    } catch (e) {
      showErrorToast(
        'Failed to save',
        e instanceof Error ? e.message : 'Could not save the assignment.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      icon="ri-file-transfer-line"
      title="Assign template"
      subtitle="Which client owns this layout. Leave blank for all clients."
      width="sm"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className={labelCls}>Template</label>
          <Badge tone="primary">{templateKey}</Badge>
        </div>

        <div>
          <label className={labelCls}>Client</label>
          <SearchableDropdown
            options={providers.options.map((p) => ({
              value: String(p.id),
              label: p.name,
            }))}
            value={clientId}
            selectedLabelFallback={providers.labelFor(clientId)}
            onChange={(next) => setClientId(next ? String(next) : '')}
            onSearchChange={providers.setSearch}
            disableLocalFilter
            allowClear
            loading={providers.loading}
            hasMore={providers.hasMore}
            onLoadMore={providers.loadMore}
            placeholder="All clients — search to assign one…"
          />
          <p className="mt-1 text-xs text-gray-400">
            Blank = available to every client (like{' '}
            <span className="font-mono">default</span>).
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default MappingFormModal;
